import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {  
  SignInResp,
  SignInVariables
} from '../../types/signInTypes';
import { getConsent } from "../../lib/consent/consent.utils";

import type { User } from '../../types/userTypes';

const SIGN_IN = gql`
  mutation Login(
    $email: String!,
    $password: String!,
    $cookieConsents: CookieConsentsInput!
  ) {
    login(
      email: $email,
      password: $password,
      cookieConsents: $cookieConsents
    ) {
      user {
        id
        email
        firstName
        lastName
        countryCode
        userConfirmed
        img {
          small
          medium
        }
      }
      errors
      notConfirmed
    }
  }
`   

type UseSignInOptions = {
  onCompleted?: (user: User) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<SignInResp, SignInVariables>>[1];
};

const useSingIn = (options?: UseSignInOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    SignInResp,
    SignInVariables
  >(SIGN_IN, options?.mutationOptions);

  const signIn = useCallback(
    async (input: SignInVariables) => {
      const consents = getConsent();
      try {
        const result = await mutate({
          variables: {
            ...input,
            cookieConsents: consents        
          }
        });

        const login = result?.data?.login;

        if (login?.errors?.length) {
          throw Object.assign(new Error(login.errors.join(", ")), {
            cause: { notConfirmed: login?.notConfirmed },
          });
        }

        if (options?.onCompleted && login?.user) {
          options.onCompleted(login.user);
        }

        return login?.user;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const user = data?.login?.user;

  return {
    signIn,
    user,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useSingIn
